
import { apiRequest, post } from "./apiService";

export type EventIssueType = 'incorrect_date' | 'incorrect_venue' | 'broken_link' | 'missing_info' | 'other';
export type PlatformIssueType = 'appearance' | 'usability' | 'feature_suggestion' | 'general';

export interface EventFeedback {
  eventId: string;
  issueType: EventIssueType | PlatformIssueType;
  description: string;
  email?: string;
  rating?: number; // Added for platform feedback
  status?: 'pending' | 'in_progress' | 'resolved';
  createdAt?: string;
  updatedAt?: string;
  id?: string;
}

export interface FeedbackResponse {
  data: {
    id: string;
    name: string;
  };
}

/**
 * Eseménnyel kapcsolatos visszajelzés beküldése
 */
export const submitEventFeedback = async (feedback: EventFeedback) => {
  try {
    console.log('Submitting feedback:', feedback);

    // Create an Asana task for the feedback
    await createAsanaTask(feedback);

    // Valós implementáció esetén:
    // return await apiRequest<EventFeedback>('/event-feedbacks', {
    //   method: 'POST',
    //   body: JSON.stringify(feedback)
    // });

    // Szimuláljuk a sikeres választ
    return {
      data: {
        ...feedback,
        id: `feedback-${Date.now()}`,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      status: 201,
      statusText: 'Created'
    };
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

/**
 * Asana feladat létrehozása a visszajelzésből
 */
export const createAsanaTask = async (feedback: EventFeedback) => {
  try {
    const isPlatformFeedback = feedback.eventId === 'PLATFORM';
    
    // Different label mappings for event and platform feedback
    const issueTypeLabels: Record<string, string> = isPlatformFeedback
      ? {
          'appearance': 'Megjelenéssel kapcsolatos',
          'usability': 'Használhatósággal kapcsolatos',
          'feature_suggestion': 'Funkció javaslat',
          'general': 'Általános visszajelzés'
        }
      : {
          'incorrect_date': 'Hibás dátum/időpont',
          'incorrect_venue': 'Hibás helyszín adatok',
          'broken_link': 'Nem működő link',
          'missing_info': 'Hiányzó információk',
          'other': 'Egyéb hiba'
        };

    const data = {
      occasionCode: feedback.eventId,
      issueType: feedback.issueType,
      description: feedback.description,
      email: feedback.email,
      // Include rating for platform feedback
      ...(isPlatformFeedback && feedback.rating ? { rating: feedback.rating } : {})
    }

    try {
      console.log("Creating feedback:", data);
      // Ensure status is set to DRAFT if not provided
      const response = await post<FeedbackResponse>('/event_feedbacks', data);
      return response.data;
    } catch (error) {
      console.error('Error creating feedback:', error);
      throw error;
    }

  } catch (error) {
    console.error('Error creating Asana task:', error);
    // Don't throw the error so the feedback submission can still proceed
    // even if the Asana integration fails
  }
};

/**
 * Visszajelzések lekérése
 */
export const getEventFeedbacks = async (eventId?: string) => {
  try {
    const endpoint = eventId
      ? `/event-feedbacks?eventId=${eventId}`
      : '/event-feedbacks';

    // Valós implementáció esetén:
    // return await apiRequest<EventFeedback[]>(endpoint);

    // Szimuláljuk a választ
    return {
      data: [],
      status: 200,
      statusText: 'OK'
    };
  } catch (error) {
    console.error('Error fetching feedbacks:', error);
    throw error;
  }
};

/**
 * Visszajelzés státuszának frissítése
 */
export const updateFeedbackStatus = async (feedbackId: string, status: 'pending' | 'in_progress' | 'resolved') => {
  try {
    // Valós implementáció esetén:
    // return await apiRequest<EventFeedback>(`/event-feedbacks/${feedbackId}`, {
    //   method: 'PATCH',
    //   body: JSON.stringify({ status })
    // });

    // Szimuláljuk a választ
    return {
      data: {
        id: feedbackId,
        status,
        updatedAt: new Date().toISOString()
      },
      status: 200,
      statusText: 'OK'
    };
  } catch (error) {
    console.error('Error updating feedback status:', error);
    throw error;
  }
};
